import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import qs from "qs";
import get from "lodash/get";
import EntityContainer from "modules/entity/containers";
import { AddButton, Pagination, Modal, ConfirmModal } from "components";
import useTranslation from "hooks/useTranslation";
import useAlert from "hooks/useAlert";
import StoreActions from "store/actions/system";
import { useDispatch } from "react-redux";
import { Skeleton } from "antd";
import { api, helpers } from "services";
import { ReactComponent as EditIcon } from "assets/images/icons/pen.svg?react";
import { ReactComponent as DeleteIcon } from "assets/images/icons/trash.svg?react";

const PREVIEW_MAX = 128;

function TemplateMiniPreview({ widthMm, heightMm, properties }) {
	const w = Math.max(Number(widthMm) || 40, 1);
	const h = Math.max(Number(heightMm) || 20, 1);
	const aspect = w / h;
	const pxH = PREVIEW_MAX / aspect;
	const list = Array.isArray(properties) ? properties : [];

	return (
		<div
			className="bc-template-preview"
			style={{
				width: PREVIEW_MAX,
				height: Math.min(pxH, 100),
				background: "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)",
				border: "1px solid #e2e8f0",
				borderRadius: 8,
				position: "relative",
				overflow: "hidden",
				boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)"
			}}
		>
			<div
				style={{
					position: "absolute",
					inset: 0,
					backgroundImage:
						"linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)",
					backgroundSize: "8px 8px",
					opacity: 0.6
				}}
			/>
			{list.map((p, i) => {
				const isBarcode = get(p, "type") === "barcode";
				const left = Math.min(100, Math.max(0, (Number(p.x_axis) / w) * 100));
				const top = Math.min(100, Math.max(0, (Number(p.y_axis) / h) * 100));
				const pw = Math.min(100, Math.max(4, (Number(p.width) / w) * 100));
				const ph = Math.min(100, Math.max(2, (Number(p.height) / h) * 100));
				return (
					<div
						key={i}
						style={{
							position: "absolute",
							left: `${left}%`,
							top: `${top}%`,
							width: `${pw}%`,
							height: `${ph}%`,
							borderRadius: 2,
							background: isBarcode
								? "repeating-linear-gradient(90deg, #334155 0px, #334155 2px, transparent 2px, transparent 4px)"
								: "rgba(59, 130, 246, 0.12)",
							border: isBarcode ? "none" : "1px solid rgba(59, 130, 246, 0.25)",
							minHeight: 4
						}}
					/>
				);
			})}
		</div>
	);
}

const BarcodeTemplatesList = () => {
	const { tr } = useTranslation();
	const { showAlert } = useAlert();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [listRefreshKey, setListRefreshKey] = useState(0);
	const [deleteModal, setDeleteModal] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [deleteSubmitting, setDeleteSubmitting] = useState(false);

	const query = useMemo(
		() => qs.parse(location.search, { ignoreQueryPrefix: true }),
		[location.search]
	);

	const pageNum = useMemo(() => {
		const p = query.page;
		if (p === undefined || p === null || p === "") return 1;
		const n = Number(p);
		return Number.isFinite(n) && n > 0 ? n : 1;
	}, [query.page]);

	const listParams = useMemo(
		() => ({
			limit: 50,
			page: pageNum,
			sort: "-id"
		}),
		[pageNum]
	);

	useEffect(() => {
		dispatch(StoreActions.SetToolbarTitle(tr("Конструктор ценников")));
		return () => {
			dispatch(StoreActions.SetToolbarTitle(""));
		};
		// tr har renderda yangi reference — [dispatch, tr] bo‘lsa SetToolbarTitle cheksiz chaqiriladi
	}, [dispatch]);

	const handlePageClick = sel => {
		const search = { ...query, page: sel + 1 };
		navigate({ search: qs.stringify(search) });
	};

	const openDeleteModal = item => {
		setSelectedTemplate(item);
		setDeleteModal(true);
	};

	const closeDeleteModal = () => {
		if (deleteSubmitting) return;
		setDeleteModal(false);
		setSelectedTemplate(null);
	};

	const confirmDeleteTemplate = async () => {
		const id = get(selectedTemplate, "id");
		if (!id) return;
		setDeleteSubmitting(true);
		try {
			await api.request({
				method: "DELETE",
				url: `/tags/${id}`,
			});
			showAlert(tr("Успешно удалено"), { type: "success" });
			setDeleteModal(false);
			setSelectedTemplate(null);
			setListRefreshKey(k => k + 1);
		} catch (err) {
			const msg =
				get(err, "response.data.message") || get(err, "message") || tr("Ошибка удаления");
			showAlert(msg, { type: "danger" });
		} finally {
			setDeleteSubmitting(false);
		}
	};

	const metaKey = useCallback(data => {
		const meta = get(data, "meta");
		if (meta && (get(meta, "current_page") != null || get(meta, "last_page") != null)) {
			return {
				currentPage:
					helpers.checkNumber(get(meta, "current_page")) ||
					helpers.checkNumber(get(data, "current_page")) ||
					1,
				pageCount:
					helpers.checkNumber(get(meta, "last_page")) ||
					helpers.checkNumber(get(data, "last_page")) ||
					1,
				perPage:
					helpers.checkNumber(get(meta, "per_page")) ||
					helpers.checkNumber(get(data, "per_page")) ||
					50,
				totalCount: helpers.checkNumber(get(meta, "total")) || helpers.checkNumber(get(data, "total"))
			};
		}
		const len = (get(data, "data") || []).length;
		return {
			currentPage: 1,
			pageCount: 1,
			perPage: len,
			totalCount: len
		};
	}, []);

	return (
		<div className="d-panel p-5 max-w-[1400px] mx-auto">
			<style>{`
				.bc-templates-grid {
					display: grid;
					grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
					gap: 20px;
				}
				.bc-templates-card {
					background: #fff;
					border-radius: 14px;
					border: 1px solid rgba(0, 0, 0, 0.06);
					box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06);
					overflow: hidden;
					transition: transform 0.2s ease, box-shadow 0.2s ease;
				}
				.bc-templates-card:hover {
					transform: translateY(-2px);
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06), 0 12px 32px rgba(15, 23, 42, 0.08);
				}
				.bc-templates-card__head {
					background: linear-gradient(120deg, #0f172a 0%, #1e293b 50%, #334155 100%);
					color: #fff;
					padding: 14px 16px;
					font-weight: 600;
					font-size: 15px;
					line-height: 1.35;
					min-height: 52px;
					display: flex;
					align-items: center;
					justify-content: space-between;
					gap: 10px;
				}
				.bc-templates-card__actions {
					display: flex;
					align-items: center;
					gap: 6px;
					flex-shrink: 0;
				}
				.bc-templates-card__actions button {
					display: inline-flex;
					align-items: center;
					justify-content: center;
					width: 32px;
					height: 32px;
					padding: 0;
					border: none;
					border-radius: 8px;
					background: rgba(255, 255, 255, 0.12);
					cursor: pointer;
					transition: background 0.15s ease;
				}
				.bc-templates-card__actions button:hover {
					background: rgba(255, 255, 255, 0.22);
				}
				.bc-templates-card__actions svg {
					width: 16px;
					height: 16px;
				}
				.bc-templates-card__actions svg path {
					fill: #fff;
				}
				.bc-templates-card__body {
					padding: 16px;
					display: flex;
					flex-direction: column;
					align-items: center;
					gap: 12px;
				}
				.bc-templates-card__meta {
					width: 100%;
					display: flex;
					flex-wrap: wrap;
					gap: 8px;
					justify-content: center;
					font-size: 12px;
					color: #64748b;
				}
				.bc-templates-card__chip {
					background: #f1f5f9;
					border-radius: 999px;
					padding: 4px 10px;
					font-weight: 500;
					color: #475569;
				}
				.bc-templates-skeleton {
					display: grid;
					grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
					gap: 20px;
				}
			`}</style>

			<div className="d-flex align-items-center justify-content-between mb-24 flex-wrap gap-3">
				<div className="main-title mb-0">{tr("Конструктор ценников")}</div>
				<AddButton
					title={tr("Создать шаблон")}
					handleClick={() => navigate("/settings/barcode-constructor/create")}
				/>
			</div>

			<Modal
				size={400}
				isOpen={deleteModal}
				toggle={closeDeleteModal}
				closeIcon={false}
			>
				<ConfirmModal
					text="Удалить шаблон ценника?"
					onConfirmHandler={confirmDeleteTemplate}
					onCancelHandler={closeDeleteModal}
					isSubmitting={deleteSubmitting}
				/>
			</Modal>

			<EntityContainer.All
				entity="price_tag_templates"
				name="all"
				url="/tags"
				primaryKey="id"
				canUpdate={listRefreshKey}
				metaKey={metaKey}
				params={listParams}
			>
				{({ items, isFetched, meta }) => {
					const pageCount = Math.max(1, get(meta, "pageCount") || 1);
					const currentPage = Math.max(1, get(meta, "currentPage") || 1);

					if (!isFetched) {
						return (
							<div className="with-bg with-pagination p-5">
								<div className="bc-templates-skeleton">
									{[1, 2, 3, 4, 5, 6].map(i => (
										<div key={i} className="bc-templates-card p-0">
											<Skeleton active paragraph={{ rows: 4 }} />
										</div>
									))}
								</div>
							</div>
						);
					}

					if (!items || items.length === 0) {
						return (
							<div className="with-bg with-pagination p-5 text-center py-40">
								<p className="text-muted mb-0 fs-16">{tr("Список пуст")}</p>
								<p className="text-muted small mt-2 mb-0">
									{tr("Создайте первый шаблон ценника")}
								</p>
							</div>
						);
					}

					return (
						<div className="with-pagination with-bg">
							<div className="bc-templates-grid p-5 pb-0">
								{items.map(item => {
									if (!item) return null;
									const name = get(item, "name") || "—";
									const w = get(item, "width");
									const h = get(item, "height", get(item, "length"));
									const props = get(item, "properties", []);
									const textCount = props.filter(p => get(p, "type") === "text").length;
									const barcodeCount = props.filter(p => get(p, "type") === "barcode").length;
									const created = get(item, "created_at");
									const dateLabel = created
										? helpers.formatDateISO8601(created, "dd.mm.yyyy HH:mm")
										: null;

									return (
										<div key={item.id} className="bc-templates-card">
											<div className="bc-templates-card__head" title={name}>
												<span className="text-truncate" style={{ maxWidth: "100%", minWidth: 0 }}>
													{name}
												</span>
												<div
													className="bc-templates-card__actions"
													onClick={e => e.stopPropagation()}
												>
													<button
														type="button"
														title={tr("Редактировать")}
														aria-label={tr("Редактировать")}
														onClick={() =>
															navigate(
																`/settings/barcode-constructor/update/${item.id}`
															)
														}
													>
														<EditIcon />
													</button>
													<button
														type="button"
														title={tr("Удалить")}
														aria-label={tr("Удалить")}
														onClick={() => openDeleteModal(item)}
													>
														<DeleteIcon />
													</button>
												</div>
											</div>
											<div className="bc-templates-card__body">
												{/* <TemplateMiniPreview
													widthMm={w}
													heightMm={h}
													properties={props}
												/> */}
												<div className="bc-templates-card__meta">
													<span className="bc-templates-card__chip">
														{w}×{h} {tr("мм")}
													</span>
													<span className="bc-templates-card__chip">
														{props.length} {tr("элементов")}
													</span>
													{textCount > 0 && (
														<span className="bc-templates-card__chip">
															{textCount} {tr("текст")}
														</span>
													)}
													{barcodeCount > 0 && (
														<span className="bc-templates-card__chip">
															{barcodeCount} {tr("штрих-код")}
														</span>
													)}
													{dateLabel && (
														<span className="bc-templates-card__chip">{dateLabel}</span>
													)}
												</div>
											</div>
										</div>
									);
								})}
							</div>
							{pageCount > 1 && (
								<div className="d-flex justify-content-center pb-20">
									<Pagination
										pageCount={pageCount}
										currentPage={currentPage}
										handlePageClick={handlePageClick}
									/>
								</div>
							)}
						</div>
					);
				}}
			</EntityContainer.All>
		</div>
	);
};

export default BarcodeTemplatesList;
